import React, { useState, useEffect, useCallback } from 'react';
import BBALoader from '../components/BBALoader';
import { LineChart, Line, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import jsPDF from 'jspdf';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getGoalsForMonth, updateGoalValues, saveMonthlyTargetsForYear, getMonthlyTargetsForYear } from '../services/goalsService';
import Modal from '../components/Modal';
import PortalBackground from '../components/PortalBackground';

const CompanyGoals = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('company');
  const [goals, setGoals] = useState({ company: [], leads: [], sales: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [editTarget, setEditTarget] = useState('');
  const [editActual, setEditActual] = useState('');
  
  // State for entries management
  const [goalEntries, setGoalEntries] = useState({});
  const [showAddEntryModal, setShowAddEntryModal] = useState(false);
  const [showEditEntriesModal, setShowEditEntriesModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [goalMonthlyTargets, setGoalMonthlyTargets] = useState({});
  const [monthlyTargetsForm, setMonthlyTargetsForm] = useState({});
  const [newEntryData, setNewEntryData] = useState({
    value: '',
    target: '',
    month: new Date().toLocaleString('default', { month: 'long' }),
    year: new Date().getFullYear().toString()
  });
  
  // State for goal tab in add entry modal
  const [addEntryModalTab, setAddEntryModalTab] = useState('progress');

  const fetchGoals = useCallback(async () => {
    setIsLoading(true);
    try {
      // Always fetch current month and year
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;
      const fetchedGoals = await getGoalsForMonth(currentYear, currentMonth);
      setGoals(fetchedGoals);

      // Load monthly targets for all goals
      const allGoals = [...fetchedGoals.company, ...fetchedGoals.leads, ...fetchedGoals.sales];
      const monthlyTargetsPromises = allGoals.map(async (goal) => {
        try {
          const targets = await getMonthlyTargetsForYear(goal.metricKey, currentYear);
          return { goalId: goal.id, targets };
        } catch (error) {
          console.error(`Error loading monthly targets for ${goal.metricKey}:`, error);
          return { goalId: goal.id, targets: {} };
        }
      });

      const monthlyTargetsResults = await Promise.all(monthlyTargetsPromises);
      const monthlyTargetsMap = {};
      monthlyTargetsResults.forEach(({ goalId, targets }) => {
        monthlyTargetsMap[String(goalId)] = {
          [String(currentYear)]: targets
        };
      });
      setGoalMonthlyTargets(prev => ({
        ...prev,
        ...monthlyTargetsMap
      }));
    } catch (error) {
      console.error("Error fetching goals: ", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const openModal = (goal) => {
    setSelectedGoal(goal);
    setEditTarget(goal.target.toString());
    setEditActual(goal.actual.toString());
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedGoal(null);
    setEditTarget('');
    setEditActual('');
  };

  // Months and years arrays for form dropdowns
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  const handleSaveGoal = async () => {
    if (!selectedGoal) return;
    
    try {
      const newTarget = parseFloat(editTarget) || 0;
      const newActual = parseFloat(editActual) || 0;
      
      await updateGoalValues(selectedGoal.id, newTarget, newActual);
      
      // Update local state
      setGoals(prevGoals => ({
        ...prevGoals,
        [selectedGoal.category]: prevGoals[selectedGoal.category].map(goal =>
          goal.id === selectedGoal.id
            ? { ...goal, target: newTarget, actual: newActual }
            : goal
        )
      }));
      
      closeModal();
    } catch (error) {
      console.error("Error updating goal:", error);
    }
  };

  // Handle edit button click
  const handleEdit = async (goal) => {
    setEditingGoal(goal);
    // Initialize monthly goals form for current year
    const yearStr = new Date().getFullYear().toString();
    const currentYear = new Date().getFullYear();
    let existing = goalMonthlyTargets[String(goal.id)]?.[yearStr] || {};
    
    // If monthly targets aren't loaded, fetch them
    if (!existing || Object.keys(existing).length === 0) {
      try {
        existing = await getMonthlyTargetsForYear(goal.metricKey, currentYear);
        // Update state with fetched targets
        setGoalMonthlyTargets(prev => ({
          ...prev,
          [String(goal.id)]: {
            ...(prev[String(goal.id)] || {}),
            [yearStr]: existing
          }
        }));
      } catch (error) {
        console.error(`Error loading monthly targets for ${goal.metricKey}:`, error);
        existing = {};
      }
    }
    
    const form = months.reduce((acc, m) => {
      acc[m] = existing[m] ?? (goal?.target ?? '');
      return acc;
    }, {});
    setMonthlyTargetsForm(form);
    setShowEditEntriesModal(true);
  };

  // Handle add entry button click
  const handleAddEntry = (goal) => {
    setEditingGoal(goal);
    setNewEntryData({
      value: '',
      target: goal?.target ?? '',
      month: new Date().toLocaleString('default', { month: 'long' }),
      year: new Date().getFullYear().toString()
    });
    setShowAddEntryModal(true);
  };

  // Handle add entry submit
  const handleAddEntrySubmit = (e) => {
    e.preventDefault();
    
    if (!editingGoal) return;
    
    const nowMonth = new Date().toLocaleString('default', { month: 'long' });
    const nowYear = new Date().getFullYear().toString();
    const monthlyTargetForNow = goalMonthlyTargets[String(editingGoal.id)]?.[nowYear]?.[nowMonth];
    const effectiveTarget = monthlyTargetForNow !== undefined && monthlyTargetForNow !== ''
      ? parseFloat(monthlyTargetForNow) || 0
      : (editingGoal?.target || 0);

    const newActual = parseFloat(newEntryData.value) || 0;
    const newEntry = {
      id: Date.now(),
      goalId: editingGoal.id,
      metricKey: editingGoal.metricKey,
      value: newActual,
      month: nowMonth,
      year: nowYear,
      createdAt: new Date().toISOString()
    };
    
    setGoalEntries(prev => ({
      ...prev,
      [String(editingGoal.id)]: [...(prev[String(editingGoal.id)] || []), newEntry]
    }));

    // Persist actual and align target to month-specific target if set
    try {
      updateGoalValues(editingGoal.id, effectiveTarget, newActual);
    } catch (err) {
      console.error('Failed to persist progress update', err);
    }

    // Update local goals state so UI reflects immediately
    setGoals(prevGoals => ({
      ...prevGoals,
      [editingGoal.category]: prevGoals[editingGoal.category].map(g =>
        g.id === editingGoal.id ? { ...g, actual: newActual, target: effectiveTarget } : g
      )
    }));
    
    setShowAddEntryModal(false);
    setEditingGoal(null);
    setNewEntryData({
      value: '',
      month: new Date().toLocaleString('default', { month: 'long' }),
      year: new Date().getFullYear().toString()
    });
  };

  // Save monthly targets for current year
  const handleSaveMonthlyTargets = async () => {
    if (!editingGoal) return;
    const currentYear = new Date().getFullYear();
    const yearStr = currentYear.toString();
    
    try {
      // Save to Firebase
      await saveMonthlyTargetsForYear(editingGoal.metricKey, currentYear, monthlyTargetsForm);
      
      // Update local state
      setGoalMonthlyTargets(prev => ({
        ...prev,
        [String(editingGoal.id)]: {
          ...(prev[String(editingGoal.id)] || {}),
          [yearStr]: monthlyTargetsForm
        }
      }));

      // Refresh goals to get updated targets
      await fetchGoals();
      
      setShowEditEntriesModal(false);
      setEditingGoal(null);
    } catch (error) {
      console.error("Error saving monthly targets:", error);
      alert("Failed to save monthly targets. Please try again.");
    }
  };

  // Handle edit entry
  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setEditingData(entry);
  };

  // Handle edit entry submit
  const handleEditEntrySubmit = (e) => {
    e.preventDefault();
    
    if (!editingEntry || !editingGoal) return;
    
    setGoalEntries(prev => ({
      ...prev,
      [String(editingGoal.id)]: (prev[String(editingGoal.id)] || []).map(entry =>
        entry.id === editingEntry.id ? editingData : entry
      )
    }));
    
    setEditingEntry(null);
    setEditingData({});
  };

  // Handle delete entry
  const handleDeleteEntry = (entryId) => {
    if (!editingGoal) return;
    
    setGoalEntries(prev => ({
      ...prev,
      [String(editingGoal.id)]: (prev[String(editingGoal.id)] || []).filter(entry => entry.id !== entryId)
    }));
  };

  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  // Calculate goal statistics from actual data
  const calculateGoalStats = () => {
    const allGoals = [...goals.company, ...goals.leads, ...goals.sales];
    const totalGoals = allGoals.length;
    const completedGoals = allGoals.filter(goal => goal.actual >= goal.target).length;
    const inProgressGoals = allGoals.filter(goal => goal.actual > 0 && goal.actual < goal.target).length;
    const notStartedGoals = allGoals.filter(goal => goal.actual === 0).length;
    const successRate = totalGoals > 0 ? Math.round((completedGoals / totalGoals) * 100) : 0;

    return {
      totalGoals,
      completedGoals,
      inProgressGoals,
      notStartedGoals,
      successRate
    };
  };

  const goalStats = calculateGoalStats();

  // Calculate department performance based on actual goals
  const calculateDepartmentPerformance = () => {
    const departments = [
      { name: 'Company', goals: goals.company },
      { name: 'Leads', goals: goals.leads },
      { name: 'Sales', goals: goals.sales }
    ];

    return departments.map(dept => {
      const totalGoals = dept.goals.length;
      if (totalGoals === 0) {
        return {
          department: dept.name,
          completed: 0,
          onTrack: 0,
          offTrack: 0,
          missed: 0
        };
      }

      const completed = dept.goals.filter(g => g.actual >= g.target).length;
      const onTrack = dept.goals.filter(g => g.actual > 0 && g.actual < g.target && (g.actual / g.target) >= 0.7).length;
      const offTrack = dept.goals.filter(g => g.actual > 0 && g.actual < g.target && (g.actual / g.target) < 0.7).length;
      const missed = dept.goals.filter(g => g.actual === 0).length;

      return {
        department: dept.name,
        completed: Math.round((completed / totalGoals) * 100),
        onTrack: Math.round((onTrack / totalGoals) * 100),
        offTrack: Math.round((offTrack / totalGoals) * 100),
        missed: Math.round((missed / totalGoals) * 100)
      };
    });
  };

  const departmentPerformance = calculateDepartmentPerformance();



  // Calculate KPI trends based on actual goal data - always show 12 months
  const calculateKPITrends = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    return months.map((month, index) => {
      const monthGoals = goals.company.filter(g => g.actual > 0);
      const successRate = monthGoals.length > 0 ? 
        Math.round(monthGoals.reduce((sum, g) => sum + (g.actual / g.target), 0) / monthGoals.length * 100) : 0;
      
      return {
        month,
        successRate,
        goalCount: monthGoals.length
      };
    });
  };

  const kpiTrends = calculateKPITrends();

  // Calculate big three goals from actual data based on active tab
  const calculateBigThreeGoals = () => {
    const formatValue = (value, unit) => {
      if (!value) return '0';
      switch (unit) {
        case 'usd':
          return `$${Number(value).toLocaleString()}`;
        case 'percent':
          return `${value}%`;
        default:
          return Number(value).toLocaleString();
      }
    };

    const getGoalData = (metricKey, collection) => {
      return collection.find(g => g.metricKey === metricKey);
    };

    const currentMonthName = months[new Date().getMonth()];
    const currentYearStr = new Date().getFullYear().toString();

    if (activeTab === 'company') {
      const mrrGoal = getGoalData('mrr', goals.company);
      const activeClientsGoal = getGoalData('activeClients', goals.company);
      const retentionGoal = getGoalData('retention', goals.company);

      const mrrTarget = mrrGoal ? (goalMonthlyTargets[String(mrrGoal.id)]?.[currentYearStr]?.[currentMonthName] ?? mrrGoal.target ?? 0) : 0;
      const activeClientsTarget = activeClientsGoal ? (goalMonthlyTargets[String(activeClientsGoal.id)]?.[currentYearStr]?.[currentMonthName] ?? activeClientsGoal.target ?? 0) : 0;
      const retentionTarget = retentionGoal ? (goalMonthlyTargets[String(retentionGoal.id)]?.[currentYearStr]?.[currentMonthName] ?? retentionGoal.target ?? 0) : 0;

      return [
        {
          name: 'Total Active Clients',
          goal: formatValue(activeClientsTarget || 0, 'count'),
          current: formatValue(activeClientsGoal?.actual || 0, 'count'),
          progress: activeClientsGoal ? (activeClientsGoal.actual / (activeClientsTarget || 1)) * 100 : 0,
          color: '#84CEEB',
          rawGoal: activeClientsGoal
        },
        {
          name: 'Client Retention Rate',
          goal: formatValue(retentionTarget || 0, 'percent'),
          current: formatValue(retentionGoal?.actual || 0, 'percent'),
          progress: retentionGoal ? (retentionGoal.actual / (retentionTarget || 1)) * 100 : 0,
          color: '#5680E9',
          rawGoal: retentionGoal
        },
        {
          name: 'Monthly Recurring Revenue',
          goal: formatValue(mrrTarget || 0, 'usd'),
          current: formatValue(mrrGoal?.actual || 0, 'usd'),
          progress: mrrGoal ? (mrrGoal.actual / (mrrTarget || 1)) * 100 : 0,
          color: '#5AB9EA',
          rawGoal: mrrGoal
        }
      ];
    } else if (activeTab === 'sales') {
      const newClientsGoal = getGoalData('newClientsSigned', goals.sales);
      const conversionGoal = getGoalData('conversionRate', goals.sales);
      const packageSizeGoal = getGoalData('avgPackageSize', goals.sales);

      const newClientsTarget = newClientsGoal ? (goalMonthlyTargets[String(newClientsGoal.id)]?.[currentYearStr]?.[currentMonthName] ?? newClientsGoal.target ?? 0) : 0;
      const conversionTarget = conversionGoal ? (goalMonthlyTargets[String(conversionGoal.id)]?.[currentYearStr]?.[currentMonthName] ?? conversionGoal.target ?? 0) : 0;
      const packageSizeTarget = packageSizeGoal ? (goalMonthlyTargets[String(packageSizeGoal.id)]?.[currentYearStr]?.[currentMonthName] ?? packageSizeGoal.target ?? 0) : 0;

      return [
        {
          name: 'New Clients Signed',
          goal: formatValue(newClientsTarget || 0, 'count'),
          current: formatValue(newClientsGoal?.actual || 0, 'count'),
          progress: newClientsGoal ? (newClientsGoal.actual / (newClientsTarget || 1)) * 100 : 0,
          color: '#5AB9EA',
          rawGoal: newClientsGoal
        },
        {
          name: 'Conversion Rate',
          goal: formatValue(conversionTarget || 0, 'percent'),
          current: formatValue(conversionGoal?.actual || 0, 'percent'),
          progress: conversionGoal ? (conversionGoal.actual / (conversionTarget || 1)) * 100 : 0,
          color: '#84CEEB',
          rawGoal: conversionGoal
        },
        {
          name: 'Average Package Size',
          goal: formatValue(packageSizeTarget || 0, 'usd'),
          current: formatValue(packageSizeGoal?.actual || 0, 'usd'),
          progress: packageSizeGoal ? (packageSizeGoal.actual / (packageSizeTarget || 1)) * 100 : 0,
          color: '#5680E9',
          rawGoal: packageSizeGoal
        }
      ];
    } else if (activeTab === 'marketing') {
      const newLeadsGoal = getGoalData('newLeads', goals.leads);
      const qualifiedLeadsGoal = getGoalData('qualifiedLeadPercent', goals.leads);
      const costPerLeadGoal = getGoalData('costPerLead', goals.leads);

      const newLeadsTarget = newLeadsGoal ? (goalMonthlyTargets[String(newLeadsGoal.id)]?.[currentYearStr]?.[currentMonthName] ?? newLeadsGoal.target ?? 0) : 0;
      const qualifiedLeadsTarget = qualifiedLeadsGoal ? (goalMonthlyTargets[String(qualifiedLeadsGoal.id)]?.[currentYearStr]?.[currentMonthName] ?? qualifiedLeadsGoal.target ?? 0) : 0;
      const costPerLeadTarget = costPerLeadGoal ? (goalMonthlyTargets[String(costPerLeadGoal.id)]?.[currentYearStr]?.[currentMonthName] ?? costPerLeadGoal.target ?? 0) : 0;

      return [
        {
          name: 'New Leads',
          goal: formatValue(newLeadsTarget || 0, 'count'),
          current: formatValue(newLeadsGoal?.actual || 0, 'count'),
          progress: newLeadsGoal ? (newLeadsGoal.actual / (newLeadsTarget || 1)) * 100 : 0,
          color: '#5AB9EA',
          rawGoal: newLeadsGoal
        },
        {
          name: 'Qualified Lead %',
          goal: formatValue(qualifiedLeadsTarget || 0, 'percent'),
          current: formatValue(qualifiedLeadsGoal?.actual || 0, 'percent'),
          progress: qualifiedLeadsGoal ? (qualifiedLeadsGoal.actual / (qualifiedLeadsTarget || 1)) * 100 : 0,
          color: '#84CEEB',
          rawGoal: qualifiedLeadsGoal
        },
        {
          name: 'Cost per Lead',
          goal: formatValue(costPerLeadTarget || 0, 'usd'),
          current: formatValue(costPerLeadGoal?.actual || 0, 'usd'),
          progress: costPerLeadGoal ? (costPerLeadGoal.actual / (costPerLeadTarget || 1)) * 100 : 0,
          color: '#5680E9',
          rawGoal: costPerLeadGoal
        }
      ];
    }

    return [];
  };

  const bigThreeGoals = calculateBigThreeGoals();

  // Calculate goal status data for pie chart
  const goalStatusData = [
    { name: 'Completed', value: goalStats.completedGoals, color: '#5AB9EA' },
    { name: 'In Progress', value: goalStats.inProgressGoals, color: '#F8A055' },
    { name: 'Not Started', value: goalStats.notStartedGoals, color: '#C1C8E4' }
  ];



  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col w-full p-6 relative">
        <PortalBackground />
        <div className="relative flex items-center justify-center h-64">
          <div className="text-center">
            <BBALoader size={48} className="mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading company goals...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col w-full p-6 relative">
      <PortalBackground />
      <div className="relative">
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/crm/reports')}
          className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white flex-1 text-center">Company Goals Report</h1>
        <div className="w-[100px]"></div>
      </div>

      {/* Tabs */}
      <div className="flex justify-center mb-6">
        <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('company')}
            className={`px-6 py-3 rounded-md font-medium transition-colors duration-200 ${
              activeTab === 'company'
                ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            Company
          </button>
          <button
            onClick={() => setActiveTab('sales')}
            className={`px-6 py-3 rounded-md font-medium transition-colors duration-200 ${
              activeTab === 'sales'
                ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            Sales
          </button>
          <button
            onClick={() => setActiveTab('marketing')}
            className={`px-6 py-3 rounded-md font-medium transition-colors duration-200 ${
              activeTab === 'marketing'
                ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            Marketing
          </button>
        </div>
      </div>





      {/* Big Three Goals */}
      <div className="mb-4">
        <h2 className="text-xl font-semibold mb-4 text-center text-gray-800 dark:text-white">
          {activeTab === 'company' && 'Company Goals'}
          {activeTab === 'sales' && 'Sales Goals'}
          {activeTab === 'marketing' && 'Marketing Goals'}
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {bigThreeGoals.map((goal, index) => (
          <div 
            key={index} 
            className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 relative"
          >
            {/* Edit Button - Top Left */}
            <button
              onClick={() => handleEdit(goal.rawGoal)}
              className="absolute top-4 left-4 p-2 rounded-full transition-colors bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>

            {/* Add Entry Button - Top Right */}
            <button
              onClick={() => handleAddEntry(goal.rawGoal)}
              className="absolute top-4 right-4 p-2 rounded-full transition-colors bg-red-100 dark:bg-red-900/20 hover:bg-red-200 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>

            <h2 
              className="text-2xl font-bold mb-6 text-gray-800 dark:text-white text-center"
            >
              {goal.name}
            </h2>
            
            
            <div className="space-y-6">
              <div>
                <div className="flex justify-between items-baseline mb-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Current Progress</span>
                  <span className="text-lg font-semibold" style={{ color: goal.color }}>{goal.current}</span>
                </div>
                <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-3">
                  <div 
                    className="h-3 rounded-full transition-all duration-500 ease-out" 
                    style={{ 
                      width: `${Math.min(100, goal.progress)}%`,
                      backgroundColor: goal.color 
                    }}
                  ></div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-gray-800/60 rounded-xl p-4 text-center">
                  <span className="text-sm text-gray-500 dark:text-gray-400 block mb-1">Goal</span>
                  <span className="text-lg font-semibold text-gray-800 dark:text-white">{goal.goal}</span>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800/60 rounded-xl p-4 text-center">
                  <span className="text-sm text-gray-500 dark:text-gray-400 block mb-1">Progress</span>
                  <span className="text-lg font-semibold text-gray-800 dark:text-white">{Math.round(goal.progress)}%</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Goal Trends */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 text-center text-gray-800 dark:text-white">Goal Trends</h2>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={kpiTrends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="month" stroke="#9CA3AF" />
              <YAxis stroke="#9CA3AF" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1F2937', 
                  border: '1px solid #374151',
                  color: '#F9FAFB'
                }}
              />
              <Legend />
              <Line type="monotone" dataKey="successRate" stroke="#5680E9" name="Success Rate %" />
              <Line type="monotone" dataKey="goalCount" stroke="#F8A055" name="Goal Count" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Goal Update Modal */}
      <Modal isOpen={isModalOpen} onClose={closeModal}>
        {selectedGoal && (
          <div className="p-6">
            <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white text-center">
              Update {selectedGoal.metricName}
            </h2>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Target
                </label>
                <input
                  type="number"
                  value={editTarget}
                  onChange={(e) => setEditTarget(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="Enter target value"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Current Actual
                </label>
                <input
                  type="number"
                  value={editActual}
                  onChange={(e) => setEditActual(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="Enter current value"
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveGoal}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors duration-200"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Add Entry Modal */}
      {showAddEntryModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center mb-4 relative">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white flex-1 text-center">
                {editingGoal?.metricName}
              </h3>
              <button
                onClick={() => setShowAddEntryModal(false)}
                className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Progress Manual Input for current date */}
              <form onSubmit={handleAddEntrySubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Value
                  </label>
                  <input
                    type="number"
                    value={newEntryData.value}
                    onChange={(e) => setNewEntryData({...newEntryData, value: e.target.value})}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 dark:bg-gray-800 dark:text-white"
                    placeholder={`Enter ${editingGoal?.unit === 'usd' ? 'amount' : editingGoal?.unit === 'percent' ? 'percentage' : 'value'}`}
                    min="0"
                    step={editingGoal?.unit === 'usd' ? '0.01' : '0.1'}
                    required
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddEntryModal(false)}
                    className="flex-1 py-2 px-4 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    Add Entry
                  </button>
                </div>
              </form>
            
          </div>
        </div>
      )}

      {/* Monthly Goals Editor Modal */}
      {showEditEntriesModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 w-full max-w-4xl mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center mb-4 relative">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white flex-1 text-center">
                {new Date().getFullYear()} Monthly Goals — {editingGoal?.metricName}
              </h3>
              <button
                onClick={() => {
                  setShowEditEntriesModal(false);
                  setEditingGoal(null);
                }}
                className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {/* 12-month goals grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {months.map((m) => (
                <div key={m} className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/60">
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    {m}
                  </label>
                  <input
                    type="number"
                    value={monthlyTargetsForm[m] ?? ''}
                    onChange={(e) => setMonthlyTargetsForm({ ...monthlyTargetsForm, [m]: e.target.value })}
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:text-white"
                    step={editingGoal?.unit === 'usd' ? '0.01' : '0.1'}
                  />
                </div>
              ))}
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowEditEntriesModal(false);
                  setEditingGoal(null);
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveMonthlyTargets}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
              >
                Save Monthly Goals
              </button>
            </div>
          </div>
        </div>
      )}

      </div>
    </div>
  );
};

export default CompanyGoals; 